import React, { useState } from 'react';

import { Checkbox, FormControlLabel, InputAdornment, styled, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import Eye from 'components/SvgComponents/icons/Eye';
import EyeOff from 'components/SvgComponents/icons/EyeOff';
import LoginSubmitButton from 'containers/LoginPage/components/LoginContinueButton';
import LoginPrivacy from 'containers/LoginPage/components/LoginPrivacy';
import LoginTextField from 'containers/LoginPage/components/LoginTextField';
import LoginContainerWithBackButton from 'containers/LoginPage/containers/LoginContainerWithBackButton';
import useAuthSession from 'hooks/useAuthSession';
import useGlobalErrorHandler from 'hooks/useGlobalErrorHandler';
import { useCreateUserInfoMutation, useRegisterMutation } from 'redux/api/registration.api';
import globalMessages from 'translations/messages/global-messages';
import sessionStorageUser from 'utils/sessionStorageUser';
import { isValidPassword } from 'utils/validators';

const LoginContainerWithBackButton_AccountCreation = styled(LoginContainerWithBackButton)(
  ({ theme }) => ({
    '& .login-account-creation-form': {
      width: 320,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: theme.spacing(8),
      gap: theme.spacing(4),
      '& .MuiTypography-caption': {
        alignSelf: 'flex-start',
      },
    },
    '& .login-account-creation-privacy-control-label': {
      margin: 0,
      gap: theme.spacing(2),
    },
  }),
);

const LoginAccountCreation = ({
  initialFirstName = '',
  initialLastName = '',
  email,
  isIDPFlow = false,
  validationCode,
  onBackButtonClick,
}) => {
  // #region [intl]
  const intl = useIntl();
  // #endregion

  // #region [auth]
  const { user, fetchAccessToken, loginWithIDP, loginWithPassword } = useAuthSession();
  // #endregion

  // #region [router]
  const history = useHistory();
  // #endregion

  // #region [states]
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    password: false,
  });
  // #endregion

  // #region [redux]
  const dispatchGlobalError = useGlobalErrorHandler();
  const [register] = useRegisterMutation();
  const [createUserInfo] = useCreateUserInfoMutation();
  // #endregion

  // #region [methods]
  const handleFirstNameChange = evt => {
    setErrors(prevErrors => ({ ...prevErrors, firstName: false }));
    setFirstName(evt.target.value);
  };

  const handleLastNameChange = evt => {
    setErrors(prevErrors => ({ ...prevErrors, lastName: false }));
    setLastName(evt.target.value);
  };

  const handlePasswordChange = evt => {
    setErrors(prevErrors => ({ ...prevErrors, password: false }));
    setPassword(evt.target.value);
  };

  const handleSubmit = async evt => {
    evt.preventDefault();
    const formErrors = {};
    if (!firstName || /^\s+$/.test(firstName)) formErrors.firstName = true;
    if (!lastName || /^\s+$/.test(lastName)) formErrors.lastName = true;
    if (!isIDPFlow && (!password || !isValidPassword(password))) formErrors.password = true;
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    try {
      setLoading(true);
      let response;
      if (isIDPFlow) {
        const accessToken = await fetchAccessToken();
        response = await createUserInfo({
          email,
          firstName,
          lastName,
          accessToken,
          userSub: user?.sub,
        }).unwrap();
        await loginWithIDP({ cacheMode: 'off' });
      } else {
        response = await register({
          email,
          password,
          firstName,
          lastName,
          validationCode,
        }).unwrap();
        await loginWithPassword(email, password);
      }
      sessionStorageUser.setIsSignUp(true);
      const links = [
        ...(response.invitations_consumed || []),
        ...(response.deep_links_consumed || []),
      ];
      if (links.length > 0) sessionStorageUser.setInvitationsLink(links);
      history.replace('/welcome');
    } catch (err) {
      dispatchGlobalError({
        error: err,
        codeToMessageIntl: {
          WRONG_REGISTRATION_PATH: globalMessages.user_already_exist_via_SSO,
        },
      });
    } finally {
      setLoading(false);
    }
  };
  // #endregion

  // #region [render]
  return (
    <LoginContainerWithBackButton_AccountCreation onBackButtonClick={onBackButtonClick}>
      <Typography variant="header" component="p">
        {intl.formatMessage(globalMessages.create_your_account)}
      </Typography>
      <form noValidate className="login-account-creation-form" onSubmit={handleSubmit}>
        <div>
          <LoginTextField
            type="text"
            value={firstName}
            autoFocus
            placeholder={intl.formatMessage(globalMessages.first_name)}
            onChange={handleFirstNameChange}
          />
          {errors.firstName && (
            <Typography variant="caption" color="error">
              {intl.formatMessage(globalMessages.required)}
            </Typography>
          )}
        </div>
        <div>
          <LoginTextField
            type="text"
            value={lastName}
            placeholder={intl.formatMessage(globalMessages.last_name)}
            onChange={handleLastNameChange}
          />
          {errors.lastName && (
            <Typography variant="caption" color="error">
              {intl.formatMessage(globalMessages.required)}
            </Typography>
          )}
        </div>
        {!isIDPFlow ? (
          <div>
            <LoginTextField
              type={showPassword ? 'text' : 'password'}
              value={password || ''}
              placeholder={intl.formatMessage(globalMessages.type_password)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? (
                      <Eye height={16} width={16} />
                    ) : (
                      <EyeOff height={16} width={16} />
                    )}
                  </InputAdornment>
                ),
              }}
              onChange={handlePasswordChange}
            />
            {errors.password && (
              <Typography variant="caption" color="error">
                {intl.formatMessage(globalMessages.password_instructions)}
              </Typography>
            )}
          </div>
        ) : null}
        <FormControlLabel
          className="login-account-creation-privacy-control-label"
          control={
            <Checkbox
              checked={privacyChecked}
              disabled={loading}
              onChange={() => setPrivacyChecked(!privacyChecked)}
            />
          }
          label={<LoginPrivacy />}
        />
        <LoginSubmitButton
          disabled={!firstName || !lastName || (!isIDPFlow && !password) || !privacyChecked}
          loading={loading}
          type="submit"
        >
          {intl.formatMessage(globalMessages.create_my_account)}
        </LoginSubmitButton>
      </form>
    </LoginContainerWithBackButton_AccountCreation>
  );
  // #endregion
};

LoginAccountCreation.propTypes = {
  initialFirstName: PropTypes.string,
  initialLastName: PropTypes.string,
  email: PropTypes.string.isRequired,
  isIDPFlow: PropTypes.bool,
  validationCode: PropTypes.string,
  onBackButtonClick: PropTypes.func.isRequired,
};

export default LoginAccountCreation;
